/**
 * Hooks LLM transverses — translate (lecture cachée) + proofread (mutation).
 *
 * `useTranslateBatch` : traduit un batch de chaînes via Claude BE
 * (`/llm/translate`). Cache TanStack Query par `[texts, target_lang]`
 * stringifié — la même œuvre re-rendue n'appelle Claude qu'une fois par
 * session. `staleTime` 1h car les descripteurs Vision ne bougent pas
 * tant que l'opératrice ne ré-analyse pas l'œuvre.
 *
 * `useProofreadField` : correction stricte on-blur (orthographe / grammaire
 * / typographie FR). Mutation — pas de cache : chaque blur réel doit
 * vérifier le texte courant. La throttle / dédoublonnage sur valeur
 * inchangée vit dans le composant caller (pour éviter les appels
 * redondants quand l'utilisateur tab+shift-tab sans modifier).
 */

import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query'

import { llmApi } from '@/utils/api/llm'
import type { ApiError } from '@/utils/api/client'
import type { ProofreadRequest, ProofreadResponse, TranslateResponse } from '@/utils/api/llm'

export type TargetLang = 'fr' | 'en'

export const translateKeys = {
  all: ['translate'] as const,
  batch: (texts: string[], targetLang: TargetLang) =>
    [...translateKeys.all, targetLang, texts] as const,
} satisfies Record<string, unknown>

export function useTranslateBatch(
  texts: string[] | null,
  targetLang: TargetLang,
): UseQueryResult<TranslateResponse, ApiError> {
  const enabled = Boolean(texts && texts.length > 0 && targetLang === 'fr')
  return useQuery<TranslateResponse, ApiError>({
    queryKey: translateKeys.batch(texts ?? [], targetLang),
    queryFn: () => llmApi.translate({ texts: texts ?? [], target_lang: targetLang }),
    enabled,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    // Un retry pour une panne passagère ; jamais pour un refus de Claude
    // (422) : la même requête reproduirait le même refus.
    retry: (failureCount, error) => error.status !== 422 && failureCount < 1,
  })
}

export function useProofreadField() {
  return useMutation<ProofreadResponse, ApiError, ProofreadRequest>({
    mutationFn: (payload) => llmApi.proofread(payload),
  })
}
